import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeContext, type ThemeValue } from './context';
import { THEME_STORAGE_KEY, storedTheme, type Theme } from './theme';
import { surfaceForPath } from './surface';

/**
 * التوكنز وحدها هي ما يتغيّر: هذا المزوّد لا يفعل غير كتابة `data-theme` على
 * `<html>`. والمظهرُ يُحسب من **سطح الشاشة** لا من تفضيل المستخدم وحده:
 * داخل المنصة تفضيلُه (نهاريٌّ افتراضاً)، وفي الموقع العام وبوابة الطفل ليليٌّ
 * دائماً — فالهوية العامة ليلية والطفل له لوحته. والطلاء الأول يسبق React عبر
 * `public/boot.js` بالقاعدة نفسها فلا يومض شيء.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const surface = surfaceForPath(pathname);
  const [theme, setThemeState] = useState<Theme>(storedTheme);
  const resolved: Theme = surface === 'app' ? theme : 'dark';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* لا تخزين — الاختيار يسري لهذه الجلسة فقط */
    }
  }, []);

  const value = useMemo<ThemeValue>(() => ({ theme, resolved, surface, setTheme }), [theme, resolved, surface, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
